<template>
  <div id="app">
    <app-top-bar></app-top-bar>
    <section class="section">
      <app-backend-error></app-backend-error>
      <router-view></router-view>
    </section>
    <app-footer-bar></app-footer-bar>
    <b-loading :is-full-page="true" :active.sync="loadingOverlay" :can-cancel="true"></b-loading>
  </div>
</template>

<script>

  import axios from 'axios';
  import md5 from 'md5';
  import TopBar from './components/TopBar';
  import FooterBar from './components/FooterBar';
  import BackendError from './components/BackendError';
  import {mapGetters, mapMutations} from 'vuex';
  import {delay, checkIfMobileUserAgent} from './utils/utils';
  import {ToastProgrammatic as Toast} from 'buefy';

  export default {
    name: 'app',
    components: {
      appTopBar: TopBar,
      appBackendError: BackendError,
      appFooterBar: FooterBar
    },
    computed: {
      ...mapGetters('auth', ['isAuth', 'token']),
      ...mapGetters('ui', ['isMenuVisible', 'loadingOverlay', 'feedback', 'automationsAdvancedViewChanged', 'clientFingerprint', 'isMobileUserAgent'])
    },
    watch: {
      feedback(value) {
        if (value) {
          Toast.open({
            type: this.feedback.type ? this.feedback.type : 'is-success',
            message: this.feedback.message
          });
        }
      }
    },
    methods: {
      ...mapMutations('ui', ['SET_UI_VERSION', 'SET_CLIENT_FINGERPRINT']),
      handleWindowResize() {
        const width = window.innerWidth;
        const height = window.innerHeight;
        this.$store.commit('ui/SET_WINDOW_SIZES', { width: width, height: height });
        if (width < 1024) {
          if (!this.automationsAdvancedViewChanged) {
            this.$store.commit('ui/SET_AUTOMATIONS_ADVANCED_VIEW', false);
          }
        }
      },
      successOnGettingCurrentPosition(position) {
        if (position && position.coords) {
          const geolocationObject = {
            coords: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            },
            timestamp: Math.floor(Date.now() / 1000)
          };
          this.$store.commit('ui/SET_GEOLOCATION', geolocationObject);
        }
      },
      failureWhileGettingCurrentPosition(err) {
        /* eslint-disable-next-line */
        console.log(`Error retrieving current position`, err);
      },
      initGeolocation() {
        if (this.isMobileUserAgent && navigator.geolocation) {
          this.$store.commit('ui/SET_GEOLOCATION_SUPPORTED', true);

          const positionFetchOptions = {
            enableHighAccuracy: true,
            maximumAge: 30000,
            timeout: 27000
          };

          navigator.geolocation.getCurrentPosition(this.successOnGettingCurrentPosition, this.failureWhileGettingCurrentPosition, positionFetchOptions);
        }
      },
      setClientFingerprint() {
        let clientBase = window.screen.width + '_' + window.screen.height;
        if (navigator && navigator.userAgent) {
          clientBase = `${navigator.userAgent}_${clientBase}`;
        }
        this['SET_CLIENT_FINGERPRINT'](md5(clientBase));
      },
      setUiVersion() {
        const { version } = require('../package.json');
        this['SET_UI_VERSION'](version);
      }
    },
    created() {
      const that = this;

      axios.interceptors.request.use((config) => {
        let needsAuth = true;
        if (config.url.match(/\/auth\/token$/) || config.url.match(/\/engine\/runbypin\/.*$/)) {
          needsAuth = false;
        }

        if (needsAuth) {
          if (this.isAuth && this.token) {
            config.headers['Butler-Ce'] = this.token;
          } else {
            return Promise.reject('Token needed, but not found (' + config.url + ')');
          }
        }
        return config;
      }, (error) => {
        return Promise.reject(error);
      });

      axios.interceptors.response.use((response) => {
        if (response && response.data) {
          if (response.data.action) {
            if (response.data.action === 'restart') {
              if (response.data.delay) {
                delay(response.data.delay)
                  .then(() => {
                    that.$store.dispatch('auth/restartFromScratch', true, { root: true });
                  });
              } else {
                that.$store.dispatch('auth/restartFromScratch', true, { root: true });
              }
            }
          }
        }
        return response;
      }, (error) => {
        return Promise.reject(error);
      });

      this.setUiVersion();

      this.setClientFingerprint();

      window.addEventListener('resize', this.handleWindowResize);

      this.handleWindowResize();
    },
    mounted() {
      const mobileUserAgent = checkIfMobileUserAgent();
      this.$store.commit('ui/SET_IS_MOBILE_USER_AGENT', mobileUserAgent);

      this.initGeolocation();
    },
    destroyed() {
      window.removeEventListener('resize', this.handleWindowResize);
    }
  }
</script>

<style lang="scss">
  @import "~bulma/sass/utilities/_all";

  $black: hsl(0, 0%, 96%);
  $black-bis: hsl(0, 0%, 93%);
  $black-ter: hsl(0, 0%, 86%);
  $grey-darker: hsl(0, 0%, 79%);
  $grey-dark: hsl(0, 0%, 71%);
  $grey: hsl(0, 0%, 52%);
  $grey-light: hsl(0, 0%, 29%);
  $grey-lighter: hsl(0, 0%, 14%);
  $white-ter: hsl(0, 0%, 4%);
  $white-bis: hsl(0, 0%, 2%);
  $white: hsl(0, 0%, 0%);
  $text: $black-bis;
  $text-strong: $black;

  $weight-light: 400;
  $weight-normal: 700;
  $weight-medum: 800;
  $weight-semibold: 900;
  $weight-bold: 1000;

  $scheme-main: $black;
  $scheme-invert: $white;
  $primary: $yellow;

  $link: $warning;
  $link-invert: $scheme-main;
  $link-hover: $scheme-main;
  $link-light: $scheme-main;
  $link-dark: $scheme-main;
  $link-visited: $scheme-main;
  $link-hover-border: $scheme-main;
  $link-focus: $scheme-main;
  $link-focus-border: $scheme-main;
  $link-active: $scheme-main;
  $link-active-border: $scheme-main;

  $checkbox-active-background-color: $scheme-main;
  $checkbox-background-color: $scheme-invert;
  $checkbox-checkmark-color: $scheme-invert;

  @import "~bulma";
  @import "~buefy/src/scss/buefy";

  #app {
    font-family: 'Avenir', Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #FFFFFF;
  }

  #app section:not(.section) {
    margin-top: 1.5rem;
  }

  #app section {
    padding-top: 1.5rem;
  }

  #app section.tab-content {
    margin-top: 0;
    padding-top: 0;
  }

  .button.is-primary, .button.is-primary:hover {
    color: #000;
  }
</style>
